import React, { useState, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Star, Quote, Sparkles } from 'lucide-react';
import { testimonials } from '../mock';

const Testimonials = () => {
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(timer);
  }, []);

  const goToPrevious = () => {
    setCurrentIndex((prev) => (prev === 0 ? testimonials.length - 1 : prev - 1));
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
  };

  const currentTestimonial = testimonials[currentIndex];

  return (
    <div className="bg-white text-gray-900 min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-4 lg:px-8 bg-gradient-to-br from-yellow-50 via-orange-50 to-red-50 overflow-hidden">
        <div className="absolute top-20 right-10 w-72 h-72 bg-yellow-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob" />
        <div className="absolute top-40 left-10 w-72 h-72 bg-orange-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-blob animation-delay-2000" />
        
        <div className="max-w-7xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-white rounded-full shadow-md mb-6">
            <Sparkles className="text-orange-600" size={18} />
            <span className="text-sm font-semibold text-gray-700">Client Success Stories</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 bg-clip-text text-transparent">
              Loved by Industry
            </span>
            <br />
            <span className="text-gray-900">Leaders Worldwide</span>
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover how we've helped businesses transform their digital presence and achieve remarkable results
          </p>
        </div>
      </section>

      {/* Featured Testimonial Slider */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="relative bg-gradient-to-br from-gray-50 to-blue-50 rounded-3xl p-12 md:p-16 shadow-2xl border-2 border-gray-200">
            <div className="absolute top-8 left-8 text-blue-200 opacity-50">
              <Quote size={64} strokeWidth={2} />
            </div>
            
            <div className="relative z-10">
              <div className="flex gap-1 justify-center mb-6">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} size={24} className="text-yellow-500" fill="currentColor" />
                ))}
              </div>

              <p className="text-2xl md:text-3xl text-gray-800 text-center mb-12 italic leading-relaxed font-medium">
                "{currentTestimonial.content}"
              </p>

              <div className="flex flex-col items-center">
                <div className="relative mb-4">
                  <div className="absolute inset-0 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full blur-lg opacity-50" />
                  <img
                    src={currentTestimonial.image}
                    alt={currentTestimonial.name}
                    className="relative w-20 h-20 rounded-full object-cover border-4 border-white shadow-xl"
                  />
                </div>
                <h4 className="text-xl font-bold text-gray-900">{currentTestimonial.name}</h4>
                <p className="text-gray-600">{currentTestimonial.role}</p>
              </div>
            </div>

            {/* Navigation Buttons */}
            <button
              onClick={goToPrevious}
              className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white hover:bg-gradient-to-br hover:from-blue-600 hover:to-purple-600 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg group"
            >
              <ChevronLeft className="text-gray-700 group-hover:text-white" size={24} />
            </button>
            <button
              onClick={goToNext}
              className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 bg-white hover:bg-gradient-to-br hover:from-blue-600 hover:to-purple-600 rounded-full flex items-center justify-center transition-all duration-300 shadow-lg group"
            >
              <ChevronRight className="text-gray-700 group-hover:text-white" size={24} />
            </button>

            {/* Dots Indicator */}
            <div className="flex justify-center gap-2 mt-8">
              {testimonials.map((_, index) => (
                <button
                  key={index}
                  onClick={() => setCurrentIndex(index)}
                  className={`transition-all duration-300 rounded-full ${
                    index === currentIndex 
                      ? 'w-8 h-3 bg-gradient-to-r from-blue-600 to-purple-600' 
                      : 'w-3 h-3 bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* All Testimonials Grid */}
      <section className="py-24 px-4 lg:px-8 bg-gradient-to-br from-gray-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              All <span className="bg-gradient-to-r from-orange-600 to-red-600 bg-clip-text text-transparent">Reviews</span>
            </h2>
            <p className="text-xl text-gray-600">What our clients are saying about us</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {testimonials.map((testimonial, index) => {
              const gradients = [
                { from: 'from-blue-500', to: 'to-cyan-500', border: 'border-blue-200' },
                { from: 'from-purple-500', to: 'to-pink-500', border: 'border-purple-200' },
                { from: 'from-orange-500', to: 'to-red-500', border: 'border-orange-200' },
                { from: 'from-green-500', to: 'to-emerald-500', border: 'border-green-200' },
              ];
              const gradient = gradients[index % gradients.length];

              return (
                <div
                  key={testimonial.id}
                  className={`group bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 border-2 ${gradient.border} hover:-translate-y-2`}
                >
                  <div className={`h-1 bg-gradient-to-r ${gradient.from} ${gradient.to} rounded-full mb-6`} />
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={18} className="text-yellow-500" fill="currentColor" />
                    ))}
                  </div>
                  <p className="text-lg text-gray-700 mb-6 italic leading-relaxed">
                    "{testimonial.content}"
                  </p>
                  <div className="flex items-center gap-4">
                    <div className="relative">
                      <div className={`absolute inset-0 bg-gradient-to-br ${gradient.from} ${gradient.to} rounded-full blur-md opacity-50`} />
                      <img
                        src={testimonial.image}
                        alt={testimonial.name}
                        className="relative w-14 h-14 rounded-full object-cover border-2 border-white"
                      />
                    </div>
                    <div>
                      <div className="font-bold text-gray-900">{testimonial.name}</div>
                      <div className="text-sm text-gray-600">{testimonial.role}</div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-4 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Trusted by <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Companies</span>
            </h2>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {[
              { value: '150+', label: 'Projects', gradient: 'from-blue-500 to-cyan-500' },
              { value: '80+', label: 'Clients', gradient: 'from-purple-500 to-pink-500' },
              { value: '98%', label: 'Satisfaction', gradient: 'from-orange-500 to-red-500' },
              { value: '5.0', label: 'Rating', gradient: 'from-green-500 to-emerald-500' },
            ].map((stat, index) => (
              <div key={index} className="text-center group">
                <div className={`text-5xl md:text-6xl font-bold bg-gradient-to-br ${stat.gradient} bg-clip-text text-transparent mb-2 group-hover:scale-110 transition-transform duration-300`}>
                  {stat.value}
                </div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default Testimonials;